import React, { useState, useEffect } from 'react';
import { licenseService, tenantService, userService } from '../services/api';

// Generate License Modal Component
export const GenerateLicenseModal = ({ onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    count: 1,
    ownerType: '',
    ownerId: '',
  });
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOwners, setLoadingOwners] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    // Fetch owners when owner type changes
    if (formData.ownerType === 'tenant') {
      fetchTenants();
    } else if (formData.ownerType === 'user') {
      fetchUsers();
    }
  }, [formData.ownerType]);

  const fetchTenants = async () => {
    setLoadingOwners(true);
    try {
      const response = await tenantService.getAll({ pageSize: 100 });
      setTenants(response.data || []);
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
      setTenants([]);
    } finally {
      setLoadingOwners(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingOwners(true);
    try {
      const response = await userService.getAll({ pageSize: 100 });
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setLoadingOwners(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.count || formData.count < 1 || formData.count > 100) {
      onError('Count must be between 1 and 100');
      return;
    }
    if (formData.ownerType && !formData.ownerId) {
      onError('Please select an owner');
      return;
    }

    setLoading(true);
    try {
      const ownerType = formData.ownerType || null;
      const ownerId = formData.ownerId || null;
      const response = await licenseService.generate(formData.count, ownerType, ownerId);
      onSuccess(response.message || `${formData.count} license(s) generated successfully`);
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to generate licenses');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🎫 Generate Licenses</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Count */}
            <div className="form-group">
              <label>Number of Licenses *</label>
              <input
                type="number"
                value={formData.count}
                onChange={e => setFormData({...formData, count: parseInt(e.target.value) || 1})}
                min={1}
                max={100}
                required
              />
              <span className="form-help">Generate 1-100 licenses at once</span>
            </div>

            {/* Owner Type */}
            <div className="form-group">
              <label>Owner Type (Optional)</label>
              <select
                value={formData.ownerType}
                onChange={e => setFormData({...formData, ownerType: e.target.value, ownerId: ''})}
              >
                <option value="">-- Leave Unowned --</option>
                <option value="user">User</option>
                <option value="tenant">Tenant</option>
              </select>
              <span className="form-help">Assign licenses to an owner or leave unowned</span>
            </div>

            {/* Owner Selection - Tenant */}
            {formData.ownerType === 'tenant' && (
              <div className="form-group">
                <label>Select Tenant *</label>
                {loadingOwners ? (
                  <div style={{ padding: '1rem', textAlign: 'center' }}>Loading tenants...</div>
                ) : (
                  <select
                    value={formData.ownerId}
                    onChange={e => setFormData({...formData, ownerId: e.target.value})}
                    required
                    size={5}
                    style={{ height: 'auto' }}
                  >
                    <option value="">-- Select a tenant --</option>
                    {tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Owner Selection - User */}
            {formData.ownerType === 'user' && (
              <div className="form-group">
                <label>Select User *</label>
                {loadingOwners ? (
                  <div style={{ padding: '1rem', textAlign: 'center' }}>Loading users...</div>
                ) : (
                  <select
                    value={formData.ownerId}
                    onChange={e => setFormData({...formData, ownerId: e.target.value})}
                    required
                    size={5}
                    style={{ height: 'auto' }}
                  >
                    <option value="">-- Select a user --</option>
                    {users.map(user => (
                      <option key={user.id || user.username} value={user.id || user.username}>
                        {user.name || user.email || user.username}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Generating...' : 'Generate Licenses'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Assign Owner Modal Component
export const AssignOwnerModal = ({ license, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    ownerType: '',
    ownerId: '',
  });
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOwners, setLoadingOwners] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    // Fetch owners when owner type changes
    if (formData.ownerType === 'tenant') {
      fetchTenants();
    } else if (formData.ownerType === 'user') {
      fetchUsers();
    }
  }, [formData.ownerType]);

  const fetchTenants = async () => {
    setLoadingOwners(true);
    try {
      const response = await tenantService.getAll({ pageSize: 100 });
      setTenants(response.data || []);
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
      setTenants([]);
    } finally {
      setLoadingOwners(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingOwners(true);
    try {
      const response = await userService.getAll({ pageSize: 100 });
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setLoadingOwners(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.ownerType) {
      onError('Please select an owner type');
      return;
    }
    if (!formData.ownerId) {
      onError('Please select an owner');
      return;
    }

    setLoading(true);
    try {
      const response = await licenseService.assignOwner(license.id, formData.ownerType, formData.ownerId);
      onSuccess(response.message || 'Owner assigned successfully');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to assign owner');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔗 Assign License Owner</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* License Info */}
            <div className="form-group">
              <label>License Key</label>
              <div className="device-preview">
                <strong style={{ fontFamily: 'monospace' }}>{license?.licenseKey || license?.id}</strong>
                {license?.status && (
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                    Status: {license.status}
                  </div>
                )}
              </div>
            </div>

            {/* Owner Type */}
            <div className="form-group">
              <label>Owner Type *</label>
              <select
                value={formData.ownerType}
                onChange={e => setFormData({...formData, ownerType: e.target.value, ownerId: ''})}
                required
              >
                <option value="">-- Select owner type --</option>
                <option value="user">User</option>
                <option value="tenant">Tenant</option>
              </select>
            </div>

            {/* Owner Selection - Tenant */}
            {formData.ownerType === 'tenant' && (
              <div className="form-group">
                <label>Select Tenant *</label>
                {loadingOwners ? (
                  <div style={{ padding: '1rem', textAlign: 'center' }}>Loading tenants...</div>
                ) : (
                  <select
                    value={formData.ownerId}
                    onChange={e => setFormData({...formData, ownerId: e.target.value})}
                    required
                    size={5}
                    style={{ height: 'auto' }}
                  >
                    <option value="">-- Select a tenant --</option>
                    {tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                )}
                <span className="form-help">License will be assigned to this tenant</span>
              </div>
            )}

            {/* Owner Selection - User */}
            {formData.ownerType === 'user' && (
              <div className="form-group">
                <label>Select User *</label>
                {loadingOwners ? (
                  <div style={{ padding: '1rem', textAlign: 'center' }}>Loading users...</div>
                ) : (
                  <select
                    value={formData.ownerId}
                    onChange={e => setFormData({...formData, ownerId: e.target.value})}
                    required
                    size={5}
                    style={{ height: 'auto' }}
                  >
                    <option value="">-- Select a user --</option>
                    {users.map(user => (
                      <option key={user.id || user.username} value={user.id || user.username}>
                        {user.name || user.email || user.username}
                      </option>
                    ))}
                  </select>
                )}
                <span className="form-help">License will be assigned to this user</span>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !formData.ownerType || !formData.ownerId}>
              {loading ? 'Assigning...' : 'Assign Owner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Revoke Confirmation Modal Component
export const RevokeConfirmModal = ({ license, onClose, onSuccess, onError }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!reason.trim()) {
      onError('Revocation reason is required');
      return;
    }

    setLoading(true);
    try {
      const response = await licenseService.revoke(license.id, reason.trim());
      onSuccess(response.message || 'License revoked successfully');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to revoke license');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚠️ Revoke License?</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p>Are you sure you want to revoke this license?</p>
            
            {/* License Info */}
            <div className="device-preview" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f5f5f5', borderRadius: '4px' }}>
              <strong style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                {license?.licenseKey || license?.id}
              </strong>
              {license?.ownerType && license?.ownerId && (
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  Owner: {license.ownerType} - {license.ownerId}
                </div>
              )}
            </div>

            <ul className="warning-list">
              <li>License will be permanently revoked</li>
              <li>Device using this license will lose activation</li>
              <li>This action cannot be easily undone</li>
              <li>A reason must be provided for audit trail</li>
            </ul>

            {/* Revocation Reason */}
            <div className="form-group">
              <label>Revocation Reason *</label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Enter reason for revoking this license..."
                rows={4}
                required
                style={{ width: '100%', resize: 'vertical' }}
              />
              <span className="form-help">Required for audit trail</span>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button 
              type="submit" 
              className="btn btn-danger" 
              disabled={loading || !reason.trim()}
            >
              {loading ? 'Revoking...' : 'Revoke License'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Deactivate Confirmation Modal Component
export const DeactivateConfirmModal = ({ license, onClose, onSuccess, onError }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      const response = await licenseService.deactivate(license.id, reason.trim() || null);
      onSuccess(response.message || 'License deactivated successfully');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to deactivate license');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚠️ Deactivate License?</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p>Are you sure you want to deactivate this license?</p>
            
            {/* License Info */}
            <div className="device-preview" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f5f5f5', borderRadius: '4px' }}>
              <strong style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                {license?.licenseKey || license?.id}
              </strong>
              {license?.ownerType && license?.ownerId && (
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  Owner: {license.ownerType} - {license.ownerId}
                </div>
              )}
            </div>

            <ul className="warning-list">
              <li>License will be temporarily deactivated</li>
              <li>Device using this license will lose activation</li>
              <li>License can be reactivated later if needed</li>
            </ul>

            {/* Optional Reason/Notes */}
            <div className="form-group">
              <label>Reason / Notes (Optional)</label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Enter optional reason or notes for deactivating..."
                rows={3}
                style={{ width: '100%', resize: 'vertical' }}
              />
              <span className="form-help">Optional notes for reference</span>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button 
              type="submit" 
              className="btn btn-danger" 
              disabled={loading}
            >
              {loading ? 'Deactivating...' : 'Deactivate License'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
